import {
  useMutation,
  UseQueryResult,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import client, {
  forBuilding,
  moreMemoryApiClient,
  throwCaughtError,
} from "../client";
import {
  ExtendedCompany,
  Company,
  ExtendedUser,
  CompanyContact,
  SSOItem,
  BronnoysundCompanyResult,
  MunuCompanyData,
  Subdivision,
  CompanyLookupResponse,
  IntegratedCompany,
} from "@/types";
import { ORG_NUMBER_REGEX } from "@/lib";
import { downloadBlobAsXlsx, downloadFile } from "@/lib";
import {
  useBuildingQuery,
  useBuildingSuspenseQuery,
} from "@/components/RouterSetup";

export const companyContactOptions = (buildingTenantId?: string) => ({
  queryKey: ["company-contact", buildingTenantId],
  queryFn: async () => {
    try {
      if (!buildingTenantId) throw new Error("buildingTenantId is required");
      const response = await client.get(
        `/getCompanyContact?buildingTenantId=${buildingTenantId}`,
      );
      if (Object.keys(response.data).length === 0) {
        return null;
      }
      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
});
export const useCompanyContact = (
  buildingTenantId?: string,
): UseSuspenseQueryResult<CompanyContact | null> => {
  return useBuildingSuspenseQuery(companyContactOptions(buildingTenantId));
};

export const companyAdminsOptions = (buildingTenantId?: string) => ({
  queryKey: ["company-admins", buildingTenantId],
  queryFn: async () => {
    try {
      if (!buildingTenantId) throw new Error("buildingTenantId is required");
      const response = await client.get(
        `/getCompanyAdmins?buildingTenantId=${buildingTenantId}`,
      );
      return response.data.users;
    } catch (error) {
      throwCaughtError(error);
    }
  },
});

export const useCompanyAdmins = (
  buildingTenantId?: string,
): UseSuspenseQueryResult<ExtendedUser[]> => {
  return useBuildingSuspenseQuery(companyAdminsOptions(buildingTenantId));
};

export const assignCompanyAccessAdmin = async (
  companyId: string,
  userId: string,
) => {
  return client.patch("/assignCompanyAccessAdmin", {
    companyId,
    userId,
  });
};

export const revokeCompanyAccessAdmin = async (
  companyId: string,
  userId: string,
) => {
  return client.patch("/revokeCompanyAccessAdmin", {
    companyId,
    userId,
  });
};

export const getCompanyFunction = async (
  buildingTenantId?: string,
  buildingId?: string,
): Promise<Company | undefined> => {
  try {
    if (!buildingTenantId) return undefined;
    const response = await client.get(
      `/getCompanyById?buildingTenantId=${buildingTenantId}`,
      forBuilding(buildingId),
    );
    return response.data;
  } catch (error) {
    throwCaughtError(error);
  }
  return undefined;
};

export const companyOptions = (buildingTenantId?: string) => ({
  queryKey: ["company", buildingTenantId],
  queryFn: () => getCompanyFunction(buildingTenantId),
  enabled: !!buildingTenantId,
});

export const useCompany = (
  buildingTenantId: string | undefined = "",
): UseSuspenseQueryResult<Company> => {
  return useBuildingSuspenseQuery({
    ...companyOptions(buildingTenantId),
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const getCompanyInAllBuildingsFunction = async (
  buildingTenantId?: string,
): Promise<Company | undefined> => {
  try {
    if (!buildingTenantId) return undefined;
    const response = await client.get(
      `/getCompanyByIdInAllBuildings?buildingTenantId=${buildingTenantId}`,
    );
    return response.data;
  } catch (error) {
    throwCaughtError(error);
  }
  return undefined;
};

export type CompanyAllBuildingsMatch = {
  buildingId: string;
  buildingTenantId: string;
  buildingTenantName: string;
  orgNumber: string;
  sharryCompanyId?: string;
  floor: string;
  entranceCount: number;
  accessControlSystemIntegration: boolean;
  userCount: number | null;
  adminCount: number | null;
};

export const searchCompaniesInAllBuildings = async (
  query: string,
): Promise<CompanyAllBuildingsMatch[]> => {
  if (query.trim().length === 0) return [];
  const response = await client.get("/searchCompaniesInAllBuildings", {
    params: { query: query.trim() },
  });
  return response.data as CompanyAllBuildingsMatch[];
};

export const companyInAllBuildingsOptions = (buildingTenantId?: string) => ({
  queryKey: ["companyInAllBuildings", buildingTenantId],
  queryFn: () => getCompanyInAllBuildingsFunction(buildingTenantId),
  enabled: !!buildingTenantId,
});

export const useCompanyInAllBuildings = (
  buildingTenantId: string | undefined = "",
): UseSuspenseQueryResult<Company> => {
  return useBuildingSuspenseQuery({
    ...companyInAllBuildingsOptions(buildingTenantId),
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};

export const useCompanyByOrgNumber = () => {
  return useMutation<CompanyLookupResponse, unknown, string | undefined>({
    mutationFn: async (orgNumber: string | undefined) => {
      const normalizedOrgNumber = orgNumber?.trim();
      if (!normalizedOrgNumber) {
        throw new Error("orgNumber is required");
      }
      const response = await client.get("/checkCompanyMatches", {
        params: { orgNumber: normalizedOrgNumber },
      });

      return response.data as CompanyLookupResponse;
    },
  });
};

export const companySubdivisionsOptions = (
  buildingTenantId?: string,
  includeUsersCount?: boolean,
) => ({
  queryKey: [
    "company-subdivisions",
    buildingTenantId,
    includeUsersCount ?? false,
  ],
  queryFn: async () => {
    try {
      const response = await client.get(
        `/getCompanySubdivisions?buildingTenantId=${buildingTenantId}&includeUsersCount=${includeUsersCount}`,
      );
      return response.data as Subdivision[];
    } catch (error) {
      console.error(error);
      return [];
    }
  },
  enabled: !!buildingTenantId,
});

export const useCompanySubdivisions = (
  buildingTenantId?: string,
  includeUsersCount?: boolean,
) => {
  return useBuildingQuery(
    companySubdivisionsOptions(buildingTenantId, includeUsersCount),
  );
};

export const subdivisionByIdOptions = (
  subdivisionId?: string,
  companyId?: string,
) => ({
  queryKey: ["subdivision-by-id", subdivisionId, companyId],
  queryFn: async (): Promise<Subdivision | null> => {
    try {
      const response = await client.get(
        `/getSubdivisionById?subdivisionId=${subdivisionId}&companyId=${companyId}`,
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
  // The endpoint requires both: subdivisions live under their company doc, and
  // interpolating a missing companyId would send the literal "undefined".
  // Callers that override `enabled` must keep both conditions.
  enabled: !!subdivisionId && !!companyId,
});

export const useSubdivisionById = (
  subdivisionId?: string,
  companyId?: string,
) => {
  return useBuildingQuery(subdivisionByIdOptions(subdivisionId, companyId));
};

export const companiesNamesOptions = (
  buildingTenantIds: string[] | null | undefined = [],
  settings?: { getAllIfNoCompanyIds?: boolean; noExternal?: boolean },
) => ({
  queryKey: [
    "companies-names",
    settings?.getAllIfNoCompanyIds,
    settings?.noExternal,
    buildingTenantIds,
  ],
  queryFn: async (): Promise<Partial<Company>[]> => {
    try {
      const companyIds = buildingTenantIds ?? []; // convert any nullish value to empty array
      if (companyIds.length === 0 && !settings?.getAllIfNoCompanyIds) {
        return [];
      }

      const response = await client.get(`/getCompaniesNames`, {
        params: {
          getAll: settings?.getAllIfNoCompanyIds,
          noExternal: settings?.noExternal,
          buildingTenantIds: companyIds,
        },
      });
      return response?.data ?? [];
    } catch (error) {
      throwCaughtError(error);
      return [];
    }
  },
  enabled:
    (buildingTenantIds ?? []).length > 0 || settings?.getAllIfNoCompanyIds,
});

export const useCompaniesNames = (
  buildingTenantIds: string[] | undefined = [],
  settings?: { getAllIfNoCompanyIds?: boolean; noExternal?: boolean },
) => {
  return useBuildingSuspenseQuery(
    companiesNamesOptions(buildingTenantIds, settings),
  );
};

export const extendedCompaniesOptions = {
  queryKey: ["extended-companies"],
  queryFn: async () => {
    try {
      const response = await moreMemoryApiClient.get("/getExtendedCompanies");
      return response.data;
    } catch (error) {
      throwCaughtError(error);
    }
  },
};

export const useExtendedCompanies = (): UseSuspenseQueryResult<
  ExtendedCompany[]
> => {
  return useBuildingSuspenseQuery(extendedCompaniesOptions);
};

// Documentation: https://data.brreg.no/enhetsregisteret/api/dokumentasjon/no/index.html#tag/Enheter/operation/hentEnheter
export const getCompaniesFromBronnoysund = async (
  query: string,
): Promise<BronnoysundCompanyResult[] | undefined> => {
  try {
    if (!query) return;
    const queryIsOrgNumber = ORG_NUMBER_REGEX.test(query);
    const queryParams =
      (queryIsOrgNumber ? "organisasjonsnummer=" : "navn=") +
      encodeURIComponent(query);
    const res = await fetch(
      `https://data.brreg.no/enhetsregisteret/api/enheter?${queryParams}&size=5`,
    );
    const data = await res.json();
    return data?._embedded?.enheter || [];
  } catch (error) {
    throwCaughtError(error);
  }
};

export const createCompanyInBuilding = async (
  companyData: Partial<Company>,
  isLinkedToGlobalCompany: boolean = false,
) => {
  return await client.post("/createCompany", {
    companyData,
    isLinkedToGlobalCompany,
  });
};

export const createSubdivision = async (
  subdivisionData: Partial<Subdivision>,
) => {
  return await client.post("/createSubdivision", subdivisionData);
};

export const updateCompanySettings = async (
  buildingTenantId: string,
  updateData: Partial<Company>,
) => {
  return await client.patch(`/updateCompany`, {
    buildingTenantId,
    updateData,
  });
};

export const updateSubdivision = async (
  companyId: string,
  subdivisionId: string,
  updateData: Partial<Subdivision>,
) => {
  return await client.patch("/updateSubdivision", {
    companyId,
    subdivisionId,
    updateData,
  });
};

export const removeUserFromCompanyAdmin = async (
  userId: string,
  companyId: string,
) => {
  return await client.patch("/removeUserFromCompanyAdmin", {
    userId,
    companyId,
  });
};

export const updateCompaniesVisitOrder = async (
  companies: { buildingTenantId: string; visitOrder: number }[],
) => {
  return await client.patch("/updateCompaniesVisitOrder", {
    companies,
  });
};

export const deleteCompany = async (companyId: string) => {
  return await client.delete("/deleteCompany", {
    data: { buildingTenantId: companyId },
  });
};

export const deleteSubdivision = async (
  subdivisionId: string,
  companyId: string,
) => {
  return await client.delete("/deleteSubdivision", {
    data: { subdivisionId, companyId },
  });
};

export const deleteSsoFromCompany = async (
  buildingTenantId: string,
  sso: SSOItem,
) => {
  return await client.delete("/deleteSso", {
    data: { buildingTenantId, sso },
  });
};

export const addSsoToCompany = async (
  buildingTenantId: string,
  companyName: string,
  realEstate: string,
  ssoSettings: SSOItem[],
) => {
  return await client.post("/addSso", {
    buildingTenantId,
    tenantName: companyName,
    realEstate,
    ssoSettings,
  });
};

export const totalUserOptions = (companyId: string | undefined) => ({
  queryKey: ["total-users", companyId],
  queryFn: async () => {
    try {
      const response = await client.get(
        `/getTotalUsersForCompany?companyId=${companyId}`,
      );
      return response.data.totalUsers;
    } catch (error) {
      throwCaughtError(error);
    }
  },
});

export const useTotalUsers = (
  companyId: string | undefined,
): UseQueryResult<number> => {
  return useBuildingQuery({
    ...totalUserOptions(companyId),
    enabled: !!companyId,
  });
};

export const companyMunuOverviewOptions = (
  companyId?: string,
  subdivisionId?: string,
) => ({
  queryKey: ["munu-company-overview", companyId, subdivisionId],
  queryFn: async () => {
    try {
      if (!companyId) return null;
      const params = new URLSearchParams({ companyId });
      if (subdivisionId) params.set("subdivisionId", subdivisionId);
      const response = await client.get(
        `/getMunuCompanyOverview?${params.toString()}`,
      );
      return response?.data ?? null;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
  enabled: !!companyId,
});

export const useCompanyMunuOverview = (companyId?: string) => {
  return useBuildingSuspenseQuery(companyMunuOverviewOptions(companyId));
};

export const companyMunuOptions = (companyId?: string) => ({
  queryKey: ["munu-company", companyId],
  queryFn: async () => {
    try {
      if (!companyId) return;
      const response = await client.get(
        `/getMunuCompanyData?companyId=${companyId}`,
      );
      return response.data as {
        activeUsers: number;
        disabledUsers: number;
        notIntegrated: number;
      };
    } catch (error) {
      throwCaughtError(error);
    }
  },
});

export const useCompanyMunuData = (
  companyId?: string,
): UseSuspenseQueryResult<MunuCompanyData> => {
  return useBuildingSuspenseQuery(companyMunuOptions(companyId));
};

export const allMunuCompaniesOptions = () => ({
  queryKey: ["all-munu-companies"],
  queryFn: async () => {
    try {
      const response = await client.get(`/getAllMunuCompanies`);
      return response.data.companies;
    } catch (error) {
      throwCaughtError(error);
    }
  },
});

export const useAllMunuCompanies = (): UseSuspenseQueryResult<
  MunuCompanyData[]
> => {
  return useBuildingSuspenseQuery(allMunuCompaniesOptions());
};

export const munuCompanyOptions = (companyId: string) => ({
  queryKey: ["munu-company", companyId],
  queryFn: async () => {
    try {
      const response = await client.get(
        `/getMunuCompany?companyId=${companyId}`,
      );
      return response.data;
    } catch (error) {
      throwCaughtError(error);
    }
  },
});

export const useMunuCompany = (
  companyId: string,
): UseSuspenseQueryResult<MunuCompanyData> => {
  return useBuildingSuspenseQuery(munuCompanyOptions(companyId));
};

export const munuVariCompanyOptions = (
  munuCompanyId: string,
  subdivisionId?: string,
) => ({
  queryKey: ["munu-vari-company", munuCompanyId, subdivisionId],
  queryFn: async () => {
    const params = new URLSearchParams({ companyId: munuCompanyId });
    if (subdivisionId) {
      params.append("subdivisionId", subdivisionId);
    }
    const response = await client.get(`/getMunuCompany?${params.toString()}`);
    return response.data;
  },
});

export const useMunuVariCompany = (munuCompanyId?: string) => {
  return useBuildingSuspenseQuery({
    ...munuVariCompanyOptions(munuCompanyId || ""),
  });
};

export const linkMunuCompany = async (
  buildingTenantId: string,
  munuId: string,
  dealIds: string[],
  subdivisionId?: string,
) => {
  return await client.post("/linkMunuCompany", {
    buildingTenantId,
    dealIds,
    munuId,
    ...(subdivisionId && { subdivisionId }),
  });
};

export const getExportedCompanyVisitors = async (
  companyId: string,
  companyName: string,
  options?: { excel?: boolean },
): Promise<File | undefined> => {
  try {
    if (!companyId) throw new Error("companyId is required");
    const response = await client.get(
      `/exportAllCompanyVisitors?companyId=${companyId}`,
      {
        responseType: "blob",
      },
    );
    const cleanCompanyName = companyName
      .toLowerCase()
      .replace(/[^a-zæøå ]/g, "")
      .replace(/\s+/g, "-");
    const timestamp = new Date().toISOString().substring(0, 10);
    const fileType = options?.excel ? "xlsx" : "csv";
    const filename = `${cleanCompanyName}_all_visitors_export_${timestamp}.${fileType}`;
    if (options?.excel) {
      await downloadBlobAsXlsx(response.data, filename);
      return;
    }
    const blob = new Blob([response.data], { type: "text/csv" });
    downloadFile(blob, filename);
    return response.data;
  } catch (error) {
    throwCaughtError(error);
  }
};

export const useIntegratedCompaniesAcs = (isAdmin: boolean = false) => {
  return useBuildingQuery<IntegratedCompany[]>({
    queryKey: ["integrated-companies-acs", isAdmin],
    queryFn: async () => {
      try {
        if (!isAdmin) {
          throw new Error("Access denied: User is not an admin");
        }
        const response = await client.get("/getIntegratedCompaniesAcs");
        return response.data;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    enabled: isAdmin,
  });
};
